import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = "https://api.myapoti.com";

const PaystackCallback = () => {
  const [status, setStatus] = useState("Verifying payment...");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDesktopPayment, setIsDesktopPayment] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const reference = params.get("reference");
      const source = params.get("source"); // desktop passes ?source=desktop

      // ── Desktop payment detected ──
      // When payment is initiated from the desktop app, Electron opens
      // Paystack in the system browser. That browser has no access token
      // and is not the app — attempting to verify or redirect is wrong.
      // Instead, show a clear message telling the user to return to the app.
      // The desktop app polls for subscription activation automatically.
      if (source === "desktop") {
        setIsDesktopPayment(true);
        setLoading(false);
        return;
      }

      // ── Also detect desktop by absence of token ──
      // If there's no token in localStorage, this is almost certainly
      // the system browser opened by Electron — not a web user.
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        setIsDesktopPayment(true);
        setLoading(false);
        return;
      }

      if (!reference) {
        setError("Invalid payment reference. Missing from URL.");
        setLoading(false);
        return;
      }

      try {
        setStatus("Verifying payment with backend...");

        const verifyRes = await axios.get(
          `${API_BASE}/paystack/subscriptions/payment/verify/${reference}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (verifyRes.data.status === "confirmed") {
          setStatus("✅ Payment confirmed! Redirecting...");
          setTimeout(() => {
            window.location.href = "/subscription";
          }, 3000);
        } else {
          setError(verifyRes.data.message || "Payment verification failed.");
          setLoading(false);
        }
      } catch (err) {
        console.error("Payment verification error:", err);
        if (err.response?.status === 401) {
          setError("Session expired. Please login again.");
          setTimeout(() => { window.location.href = "/login"; }, 2000);
        } else if (err.response?.status === 404) {
          setError("Payment not found. Please try again.");
        } else {
          setError(err.response?.data?.detail || "Payment verification failed. Please try again.");
        }
        setLoading(false);
      }
    };

    verifyPayment();
  }, []);

  // ── Desktop payment — show return-to-app message ──
  // No API calls, no redirects, no login checks.
  // The desktop app is already polling and will update automatically.
  if (isDesktopPayment) {
    return (
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "center",
        minHeight: "100vh", background: "#f0f9f0",
      }}>
        <div style={{
          background: "white", padding: "2.5rem", borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)", textAlign: "center",
          maxWidth: "500px", width: "90%",
        }}>
          {/* Success icon */}
          <div style={{
            width: "80px", height: "80px", background: "#d4edda",
            borderRadius: "50%", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 1.5rem", fontSize: "2.5rem",
          }}>
            ✅
          </div>

          <h2 style={{ color: "#155724", marginBottom: "0.75rem", fontSize: "1.6rem" }}>
            Payment Complete!
          </h2>

          <p style={{ color: "#555", lineHeight: "1.7", marginBottom: "1.5rem", fontSize: "1rem" }}>
            Your payment was received successfully.
          </p>

          {/* Main instruction box */}
          <div style={{
            background: "#e7f3ff", border: "2px solid #007bff", borderRadius: "12px",
            padding: "1.25rem 1.5rem", marginBottom: "1.5rem",
          }}>
            <p style={{ margin: "0 0 0.5rem", fontWeight: "700", color: "#0056b3", fontSize: "1.1rem" }}>
              👈 Return to the MyApoti Desktop App
            </p>
            <p style={{ margin: 0, color: "#0056b3", fontSize: "0.9rem", lineHeight: "1.6" }}>
              Your subscription is being activated automatically in the app.
              You can close this browser tab and switch back to MyApoti on your computer.
            </p>
          </div>

          {/* Secondary info */}
          <div style={{
            background: "#f8f9fa", borderRadius: "8px",
            padding: "1rem", marginBottom: "1rem",
          }}>
            <p style={{ margin: "0.4rem 0", color: "#555", fontSize: "0.88rem" }}>
              ✓ Payment received by Paystack
            </p>
            <p style={{ margin: "0.4rem 0", color: "#555", fontSize: "0.88rem" }}>
              ✓ Subscription activating in the desktop app
            </p>
            <p style={{ margin: "0.4rem 0", color: "#555", fontSize: "0.88rem" }}>
              ✓ You can close this browser tab safely
            </p>
          </div>

          <p style={{ color: "#999", fontSize: "0.82rem", margin: 0 }}>
            If your subscription does not update within 2 minutes,
            click the sync button in the app or restart MyApoti.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      minHeight: "100vh", background: "#f5f5f5",
    }}>
      <div style={{
        background: "white", padding: "2rem", borderRadius: "12px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)", textAlign: "center",
        maxWidth: "500px", width: "90%",
      }}>
        {loading ? (
          <>
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{
                width: "60px", height: "60px", margin: "0 auto 1rem",
                border: "4px solid #f0f0f0", borderTop: "4px solid #007bff",
                borderRadius: "50%", animation: "spin 1s linear infinite",
              }} />
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>

            <h2 style={{ color: "#333", marginBottom: "1rem", fontSize: "1.5rem" }}>{status}</h2>
            <p style={{ color: "#666", marginBottom: "2rem", lineHeight: "1.6" }}>
              Please wait while we verify your payment and activate your subscription.
              <br /><strong>Do not close this page.</strong>
            </p>

            <div style={{
              background: "#e7f3ff", border: "1px solid #b3d9ff",
              borderRadius: "8px", padding: "1rem", color: "#0056b3", fontSize: "0.9rem",
            }}>
              <p style={{ margin: "0.5rem 0" }}>✓ Payment verification in progress</p>
              <p style={{ margin: "0.5rem 0" }}>✓ Activating your subscription</p>
              <p style={{ margin: "0.5rem 0" }}>✓ You will be redirected automatically</p>
            </div>
          </>
        ) : error ? (
          <>
            <div style={{ marginBottom: "1.5rem", fontSize: "3rem" }}>❌</div>
            <h2 style={{ color: "#dc3545", marginBottom: "1rem", fontSize: "1.5rem" }}>Payment Error</h2>
            <p style={{ color: "#666", marginBottom: "2rem", lineHeight: "1.6" }}>{error}</p>

            <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => window.location.href = "/subscription"}
                style={{ padding: "0.75rem 1.5rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "1rem", fontWeight: "500" }}
              >
                Back to Subscription
              </button>
              <button
                onClick={() => window.location.href = "/login"}
                style={{ padding: "0.75rem 1.5rem", background: "#6c757d", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "1rem", fontWeight: "500" }}
              >
                Login Again
              </button>
            </div>

            <div style={{
              marginTop: "2rem", padding: "1rem", background: "#f8d7da",
              border: "1px solid #f5c6cb", borderRadius: "8px",
              color: "#721c24", fontSize: "0.85rem",
            }}>
              <p style={{ margin: "0.5rem 0", fontWeight: "500" }}>What to do next:</p>
              <ul style={{ textAlign: "left", marginLeft: "1rem" }}>
                <li>Check your internet connection</li>
                <li>Refresh the page and try again</li>
                <li>Go back to subscription and retry</li>
                <li>Contact support if problem persists</li>
              </ul>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PaystackCallback;